import { useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Users, ChevronDown, ChevronUp, CheckCircle2, Star, HelpCircle, Loader2, Download } from "lucide-react";

const GROUPS = [
  { key: "attending", label: "Attending", icon: CheckCircle2, color: "text-calm" },
  { key: "interested", label: "Interested", icon: Star, color: "text-clinical" },
  { key: "tentative", label: "Tentative", icon: HelpCircle, color: "text-alert" },
];

export function RsvpRoster({ fetchUrl, testid = "roster" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!data) {
      setBusy(true);
      try { const res = await api.get(fetchUrl); setData(res.data); }
      catch (e) { toast.error(apiErr(e)); setOpen(false); }
      finally { setBusy(false); }
    }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`${fetchUrl}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "attendees.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Attendee list downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setDownloading(false); }
  };

  const total = data ? GROUPS.reduce((n, g) => n + (data[g.key]?.length || 0), 0) : 0;

  return (
    <div className="mt-2 border-t border-line pt-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <button type="button" data-testid={`${testid}-toggle`} onClick={toggle}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted2 hover:text-clinical">
          <Users className="w-3.5 h-3.5" /> Attendee roster
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button type="button" data-testid={`${testid}-csv`} onClick={downloadCsv} disabled={downloading}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-clinical border border-clinical/30 rounded-md px-2.5 py-1 hover:bg-clinical/5 disabled:opacity-50">
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} CSV
        </button>
      </div>
      {open && (
        <div className="mt-2" data-testid={`${testid}-body`}>
          {busy && <span className="inline-flex items-center gap-1 text-xs text-muted2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</span>}
          {!busy && data && total === 0 && (
            <p className="text-xs text-muted2">No registered members yet. Logged-in patients who RSVP will appear here.</p>
          )}
          {!busy && data && total > 0 && (
            <div className="grid gap-2 sm:grid-cols-3">
              {GROUPS.map((g) => (
                <div key={g.key} data-testid={`${testid}-${g.key}`}>
                  <div className={`flex items-center gap-1 text-xs font-bold ${g.color} mb-1`}>
                    <g.icon className="w-3.5 h-3.5" /> {g.label} ({data[g.key]?.length || 0})
                  </div>
                  <ul className="space-y-0.5">
                    {(data[g.key] || []).map((p, i) => (
                      <li key={p.email || p.name || `p-${i}`} className="text-xs text-ink truncate" title={p.email || p.phone || ""}>{p.name}</li>
                    ))}
                    {(data[g.key]?.length || 0) === 0 && <li className="text-xs text-muted2">—</li>}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
